import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShowPopup, useHapticFeedback } from '@vkruglikov/react-telegram-web-app';
import { useNavigate } from 'react-router-dom';

import { CustomModal } from '../components/CustomModal';
import { BackgroundStars } from '../components/BackgroundStars';
import { NavigationBar } from '../components/NavigationBar';
import { useGameStore } from '../store/gameStore';
import { giftGameAPI, GiftCellResult, GiftGameSession } from '../services/api';

type GameStatus = 'playing' | 'won' | 'lost';
type CellState = 'hidden' | 'safe' | 'trap' | 'gift';

const DEFAULT_GRID = 5;
const DEFAULT_TRAPS = 8;
const DEFAULT_ATTEMPTS = 3;

const normalizeOpenedCells = (opened: unknown): Array<{ index: number; result: GiftCellResult }> => {
  if (Array.isArray(opened)) {
    return opened as Array<{ index: number; result: GiftCellResult }>;
  }
  if (typeof opened === 'string') {
    try {
      const parsed = JSON.parse(opened);
      return Array.isArray(parsed)
        ? (parsed as Array<{ index: number; result: GiftCellResult }>)
        : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

const mergeOpenedCells = (
  grid: CellState[],
  openedRaw: unknown,
): Array<{ index: number; result: GiftCellResult }> => {
  const merged = new Map<number, GiftCellResult>();
  normalizeOpenedCells(openedRaw).forEach((cell) => {
    merged.set(cell.index, cell.result);
  });
  grid.forEach((state, index) => {
    if (state !== 'hidden' && !merged.has(index)) {
      merged.set(index, state);
    }
  });
  return Array.from(merged, ([index, result]) => ({ index, result }));
};

const buildGrid = (gridSize: number, openedRaw: unknown) => {
  const total = gridSize * gridSize;
  const base = Array.from({ length: total }, () => 'hidden' as CellState);
  const opened = normalizeOpenedCells(openedRaw);
  opened.forEach((cell) => {
    if (cell.index >= 0 && cell.index < total) {
      base[cell.index] = cell.result;
    }
  });
  return base;
};

const MainPage: React.FC = () => {
  const [gridSize, setGridSize] = useState(DEFAULT_GRID);
  const [attemptsLeft, setAttemptsLeft] = useState(DEFAULT_ATTEMPTS);
  const [attemptsTotal, setAttemptsTotal] = useState(DEFAULT_ATTEMPTS);
  const [grid, setGrid] = useState<CellState[]>(() => buildGrid(DEFAULT_GRID, []));

  const [gameId, setGameId] = useState<string | null>(null);
  const [commitHash, setCommitHash] = useState<string>('');
  const [status, setStatus] = useState<GameStatus>('playing');
  const [isProcessing, setIsProcessing] = useState(false);
  const [reward, setReward] = useState<number>(0);
  const [seed, setSeed] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [lastEffect, setLastEffect] = useState<{ index: number; result: GiftCellResult } | null>(null);
  const effectTimer = useRef<number | null>(null);

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string;
    buttons?: Array<{ id: string; text: string; type?: string }>;
    onClose?: (buttonId: string) => void;
  }>({
    title: '',
    message: '',
  });

  const navigate = useNavigate();
  const gameStore = useGameStore();
  const { stars, tickets, addStars, fetchUserData, syncPendingClicks, setTickets } = gameStore;

  useEffect(() => {
    fetchUserData();
    syncPendingClicks();
  }, [fetchUserData, syncPendingClicks]);

  const telegramShowPopup = useShowPopup();
  const [telegramImpactOccurred] = useHapticFeedback();

  const showPopup = useCallback(
    (params: any) => {
      try {
        return telegramShowPopup(params);
      } catch (e) {
        return new Promise<string>((resolve) => {
          setModalConfig({
            title: params.title || 'Уведомление',
            message: params.message,
            buttons: params.buttons || [],
            onClose: (buttonId: string) => {
              setModalIsOpen(false);
              resolve(buttonId);
            },
          });
          setModalIsOpen(true);
        });
      }
    },
    [telegramShowPopup],
  );

  const impactOccurred = useCallback(
    (style: any) => {
      try {
        telegramImpactOccurred(style);
      } catch (e) {
        // ignore
      }
    },
    [telegramImpactOccurred],
  );

  const applySession = useCallback((session: GiftGameSession) => {
    setGameId(session.gameId);
    setGridSize(session.gridSize);
    setAttemptsLeft(session.attemptsLeft);
    setAttemptsTotal(session.attempts);
    setCommitHash(session.commitHash);
    setStatus(session.status);
    setGrid(buildGrid(session.gridSize, session.openedCells));
  }, []);

  const startGame = useCallback(
    async (forceNew = false) => {
      if (forceNew && tickets <= 0) {
        await showPopup({
          title: 'Нет билетиков',
          message: 'Чтобы начать игру, получите билетики в заданиях.',
          buttons: [{ id: 'ok', type: 'default', text: 'Понятно' }],
        });
        return;
      }

      setIsProcessing(true);
      try {
        const response = await giftGameAPI.start({
          gridSize: DEFAULT_GRID,
          traps: DEFAULT_TRAPS,
          attempts: DEFAULT_ATTEMPTS,
          forceNew,
        });
        if (response?.tickets !== undefined) {
          setTickets(response.tickets);
        }
        if (response?.session) {
          applySession(response.session);
          setReward(0);
          setSeed(null);
          setClaimed(false);
        }
      } catch (error: any) {
        const apiError = error?.response?.data;
        if (apiError?.error === 'NO_TICKETS') {
          if (apiError.tickets !== undefined) {
            setTickets(apiError.tickets);
          }
          if (forceNew) {
            await showPopup({
              title: 'Нет билетиков',
              message: 'Чтобы начать игру, получите билетики в заданиях.',
              buttons: [{ id: 'ok', type: 'default', text: 'Понятно' }],
            });
          }
        } else {
          console.error('Failed to start gift game:', error);
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [applySession, setTickets, showPopup, tickets],
  );

  useEffect(() => {
    startGame(false);
  }, [startGame]);

  useEffect(() => {
    if (!lastEffect) return;
    if (effectTimer.current) {
      window.clearTimeout(effectTimer.current);
    }
    effectTimer.current = window.setTimeout(() => {
      setLastEffect(null);
    }, 520);
  }, [lastEffect]);

  const handleCellClick = useCallback(
    async (index: number) => {
      if (isProcessing || status !== 'playing' || !gameId) return;
      if (grid[index] !== 'hidden') return;

      setIsProcessing(true);
      try {
        const response = await giftGameAPI.click({ gameId, cellIndex: index });
        if (!response?.success) return;

        setAttemptsLeft(response.attemptsLeft);
        setStatus(response.status);
        setGrid(buildGrid(gridSize, mergeOpenedCells(grid, response.openedCells)));

        if (response.result) {
          setLastEffect({ index, result: response.result });
          if (response.result === 'trap') {
            impactOccurred('error');
          } else if (response.result === 'gift') {
            impactOccurred('success');
          } else {
            impactOccurred('light');
          }
        }

        if (response.finished) {
          const rewardAmount = response.reward || 0;
          setReward(rewardAmount);

          if (response.status === 'won') {
            await showPopup({
              title: 'Подарок найден! 🎁',
              message: `Вы нашли подарок и получили ${rewardAmount} ⭐`,
              buttons: [{ id: 'ok', type: 'default', text: 'Ура!' }],
            });
          } else if (response.status === 'lost') {
            const rewardLine = rewardAmount > 0 ? `\nВы получили ${rewardAmount} ⭐` : '';
            if (response.result === 'trap') {
              await showPopup({
                title: 'Вы попали на ловушку ❌',
                message: `Игра завершена.${rewardLine}`,
                buttons: [{ id: 'ok', type: 'default', text: 'Понятно' }],
              });
            } else {
              await showPopup({
                title: 'Попытки закончились',
                message: `Подарок спрятан в одном из оставшихся квадратов.${rewardLine}`,
                buttons: [{ id: 'ok', type: 'default', text: 'Понятно' }],
              });
            }
          }

          if (!claimed) {
            try {
              const claimResponse = await giftGameAPI.claim({ gameId });
              if (claimResponse?.success) {
                const gained = claimResponse.reward || 0;
                if (gained > 0) {
                  addStars(gained);
                }
                setClaimed(true);
              }
            } catch (error) {
              console.error('Gift auto-claim failed:', error);
            }
          }
        }

        if (response.seed) {
          setSeed(response.seed);
        }
      } catch (error) {
        console.error('Gift game click failed:', error);
      } finally {
        setIsProcessing(false);
      }
    },
    [addStars, claimed, gameId, grid, gridSize, impactOccurred, isProcessing, showPopup, status],
  );

  const handleClaimReward = useCallback(async () => {
    if (!gameId || status === 'playing' || claimed) return;
    try {
      const response = await giftGameAPI.claim({ gameId });
      if (response?.success) {
        const gained = response.reward || 0;
        if (gained > 0) {
          addStars(gained);
        }
        setClaimed(true);
        await showPopup({
          title: 'Награда получена',
          message: `+${gained} ⭐ на баланс`,
          buttons: [{ id: 'ok', type: 'default', text: 'Ок' }],
        });
      }
    } catch (error) {
      console.error('Gift claim failed:', error);
    }
  }, [addStars, claimed, gameId, showPopup, status]);

  const handleVerify = useCallback(async () => {
    if (!gameId) return;
    try {
      const response = await giftGameAPI.verify({ gameId });
      if (response?.success) {
        await showPopup({
          title: 'Проверка честности',
          message: `Commit: ${response.commitHash}\nSeed: ${response.seed}`,
          buttons: [{ id: 'ok', type: 'default', text: 'Понятно' }],
        });
      }
    } catch (error) {
      console.error('Gift verify failed:', error);
    }
  }, [gameId, showPopup]);

  const handleMissionTask = useCallback(() => {
    navigate('/tasks');
  }, [navigate]);

  const progressPercent = useMemo(() => {
    if (attemptsTotal === 0) return 0;
    return Math.max(0, Math.min(100, (attemptsLeft / attemptsTotal) * 100));
  }, [attemptsLeft, attemptsTotal]);

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="app-title">SaperStars</div>
        <div className="app-score">
          <div className="score-group">
            <span className="score-value">{tickets}</span>
            <span className="score-icon">🎟️</span>
          </div>
          <div className="score-group">
            <span className="score-value">{stars.toFixed(1)}</span>
            <span className="score-icon">⭐</span>
          </div>
        </div>
      </header>

      <div className="content">
        <BackgroundStars />

        <section className="gift-game-panel">
          <div className="gift-game-header">
            <div>
              <div className="gift-game-title">🎁 Найди, где спрятан подарок!</div>
              <div className="gift-game-subtitle">Осталось попыток: {attemptsLeft}</div>
            </div>
            <img className="gift-game-mascot" src="/img/Plush%20Pepe.webp" alt="Pepe" />
          </div>
          <div className="gift-progress">
            <div className="gift-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="gift-game-commit">Commit: {commitHash.slice(0, 12)}…</div>
        </section>

        <div
          className="gift-grid"
          style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)` }}
        >
          {grid.map((cell, index) => {
            const effectClass =
              lastEffect && lastEffect.index === index ? `effect-${lastEffect.result}` : '';
            return (
              <button
                key={index}
                type="button"
                className={`gift-cell ${cell} ${effectClass}`}
                onClick={() => handleCellClick(index)}
                disabled={isProcessing || status !== 'playing' || cell !== 'hidden'}
                aria-label={`gift-cell-${index}`}
              >
                <span className="gift-cell-icon">
                  {cell === 'trap' ? '❌' : cell === 'gift' ? '🎁' : cell === 'safe' ? '⭐' : ''}
                </span>
              </button>
            );
          })}
        </div>

        <div className="gift-game-actions">
          <button className="gift-game-button" onClick={() => startGame(true)}>
            Новая игра
          </button>
          <button className="gift-game-button ghost" onClick={handleMissionTask}>
            Задания
          </button>
        </div>

        {status !== 'playing' && (
          <div className="gift-game-reward">
            <span>Награда: {reward} ⭐</span>
            <button
              className="gift-game-button claim"
              onClick={handleClaimReward}
              disabled={claimed || reward === 0}
            >
              {reward === 0 ? 'Награды нет' : claimed ? 'Награда получена' : 'Получить награду'}
            </button>
          </div>
        )}

        {status !== 'playing' && seed && (
          <button className="gift-game-verify" onClick={handleVerify}>
            Проверить честность
          </button>
        )}

        {/* Модальное окно */}
        <CustomModal
          isOpen={modalIsOpen}
          title={modalConfig.title}
          message={modalConfig.message}
          buttons={modalConfig.buttons}
          onClose={(buttonId) => modalConfig.onClose?.(buttonId)}
        />
      </div>

      <NavigationBar />
    </div>
  );
};

export default MainPage;